import React from "react";
import {
  Alert,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Badge,
} from "reactstrap";
import { gql } from "apollo-boost";
import { useQuery, useSubscription } from "@apollo/react-hooks";
import { IsErrorOrLoading } from "../utils/IsErrorOrLoading";
import { delegateLogo } from "../utils/logos/DelegateLogo";
import { Link } from "react-router-dom";
import { fromLiskEpoch } from "../utils/lisk/utils/lisk/time";
import moment from "moment";
import { beddowsToDecimal } from "../utils/lisk/utils/lisk/beddowsToDecimal";
import { Stars } from "../../UI/Stars";
import { VotersUnvoters } from "../../UI/components/VotersUnvoters";

const avatarStyleVote = {
  width: "25px",
  height: "25px",
  borderRadius: "25px",
  marginRight: "10px",
};
const avatarStyleSignup = {
  width: "50px",
  height: "50px",
  borderRadius: "50px",
  marginRight: "10px",
};

const LAST_DELEGATE_SIGNUPS_QUERY = gql`
  subscription lastDelegateSignups {
    transactions_public(
      order_by: { timestamp: desc }
      where: { type: { _eq: "2" } }
    ) {
      asset
      timestamp
    }
  }
`;
const ALL_DELEGATES_QUERY = gql`
  query lastTransactions {
    accounts_public(
      where: { username: {}, _not: { username: { _eq: "null" } } }
    ) {
      username
      publicKey
    }
  }
`;
const LAST_VOTES_SUBSCRIPTION = gql`
  subscription lastTransactions {
    transactions_public(
      limit: 20
      where: { type: { _eq: "3" } }
      offset: 0
      order_by: { timestamp: desc }
    ) {
      id
      type
      timestamp
      sender {
        address
        username
      }
      sender_mem {
        balance
      }
      senderId
      asset
      fee
    }
  }
`;

export const LastVotes: React.FC = () => {
  const {
    data: lastVotes,
    loading: lastSignupsLoading,
    error: lastSignupsError,
  } = useSubscription(LAST_DELEGATE_SIGNUPS_QUERY);
  const {
    data: allDelegatesData,
    loading: allDelegatesLoading,
    error: allDelegatesError,
  } = useQuery(ALL_DELEGATES_QUERY);
  const {
    data: lastVotesData,
    loading: lastVotesLoading,
    error: lastVotesError,
  } = useSubscription(LAST_VOTES_SUBSCRIPTION);

  if (
    allDelegatesError ||
    allDelegatesLoading ||
    lastVotesError ||
    lastVotesLoading ||
    lastSignupsLoading ||
    lastSignupsError
  ) {
    return (
      <div className="content">
        <IsErrorOrLoading
          error={!!allDelegatesError || !!lastVotesError || !!lastSignupsError}
          title={"network events"}
        />
      </div>
    );
  }

  const delegates = [...allDelegatesData.accounts_public];
  const votes = [...lastVotesData.transactions_public];
  const signups = [...lastVotes.transactions_public];

  const result = votes.map((vote) => {
    vote.votesNames = vote.asset.votes.map((pk: any) => {
      const name = delegates.find((d) => {
        return d.publicKey === pk.slice(1, pk.length);
      });
      name.kind = pk.slice(0, 1) === "+" ? "vote" : "unvote";
      return { ...name };
    });

    return vote;
  });

  return (
    <div className="content">
      <Stars />
      <div className="react-notification-alert-container"></div>
      <Row>
        <Col md="6">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">
                Network events:
                <p className="category">Recent votes</p>
              </CardTitle>
            </CardHeader>
            <CardBody>
              {result.map((vote, i) => {
                const weight =
                  Math.round(
                    +beddowsToDecimal(
                      vote.sender_mem.balance
                    ).toLocaleString() * 100
                  ) / 100;
                let votes = 0;
                for (let i in vote.votesNames)
                  if (vote.votesNames[i].kind == "vote") votes++;
                const mdVotes = Math.floor(
                  (votes * 100) / vote.votesNames.length / 10
                );
                const txVotes = vote.votesNames
                  .map((v: any) => (v.kind === "vote" ? v.username : null))
                  .filter((a: any) => a !== null)
                  .join(" - ");

                const txUnvotes = vote.votesNames
                  .map((v: any) => (v.kind === "unvote" ? v.username : null))
                  .filter((a: any) => a !== null)
                  .join(" - ");
                return (
                  <Alert style={{ background: "none" }} key={i}>
                    <Row>
                      <Col md="12" style={{ marginBottom: "10px" }}>
                        <div>
                          {" "}
                          {delegateLogo({
                            delegateName: vote.sender
                              ? vote.sender.username
                              : null,
                            style: avatarStyleVote,
                            address: vote.sender
                              ? vote.sender.address
                              : vote.senderId,
                            generateRandom: true,
                          })}{" "}
                          <Link
                            className="text-info"
                            to={`/account/${vote.senderId}`}
                          >
                            {(vote.sender && vote.sender.username) ||
                              vote.senderId}{" "}
                            -
                            <Badge
                              className={"badge-dark"}
                              style={{ fontSize: "medium" }}
                            >
                              {weight} Ⱡ
                            </Badge>
                            - {moment(fromLiskEpoch(vote.timestamp)).fromNow()}
                          </Link>
                        </div>
                      </Col>
                    </Row>
                    <VotersUnvoters
                      mdVotes={mdVotes}
                      txVotes={txVotes}
                      txUnvotes={txUnvotes}
                    ></VotersUnvoters>
                  </Alert>
                );
              })}
            </CardBody>
          </Card>
        </Col>
        <Col md="6">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">
                Network events:
                <p className="category">Recent delegate registrations</p>
              </CardTitle>
            </CardHeader>
            <CardBody>
              {signups.map((delegate, i) => {
                const delegateName = delegate.asset.delegate.username;
                return (
                  <Alert color="info" key={i}>
                    <Row>
                      <Col md="12">
                        <div>
                          <Link to={`/account/${delegateName}`}>
                            <span
                              style={{
                                color: "#ffffffd9",
                                fontWeight: "bold",
                                textDecoration: "underline",
                              }}
                            >
                              {delegateLogo({
                                delegateName: delegateName,
                                style: avatarStyleSignup,
                              })}
                              {delegateName}
                            </span>
                          </Link>
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12} className={"text-right font-italic"}>
                        {moment(fromLiskEpoch(delegate.timestamp)).fromNow()}
                      </Col>
                    </Row>
                  </Alert>
                );
              })}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
